import React from 'react'
import { HeadContent, Scripts, createRootRoute } from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { TanStackDevtools } from '@tanstack/react-devtools'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'sonner'
import appCss from '../styles.css?url'
import type { ErrorInfo, ReactNode } from 'react'
import { BottomNav } from '@/components/bottom-nav'
import { PWAUpdatePrompt } from '@/components/pwa-update-prompt'
import { ThemeProvider } from '@/lib/theme'
import { AuthProvider } from '@/lib/auth-oidc'
import { LanguageProvider } from '@/lib/language'
import { QueryProvider } from '@/lib/query-provider'
import { initObservability } from '@/lib/observability'
import '@/lib/i18n' // Initialize i18n

// Initialize observability (tracing + session replay)
if (typeof window !== 'undefined') {
  initObservability()
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content:
          'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover',
      },
      {
        title: 'Mashinato PWA',
      },
      {
        name: 'description',
        content: 'A modern Progressive Web App with mobile-first design',
      },
      {
        name: 'theme-color',
        content: '#000000',
      },
      {
        name: 'apple-mobile-web-app-capable',
        content: 'yes',
      },
      {
        name: 'apple-mobile-web-app-status-bar-style',
        content: 'black-translucent',
      },
      {
        name: 'apple-mobile-web-app-title',
        content: 'Mashinato',
      },
    ],
    links: [
      {
        rel: 'stylesheet',
        href: appCss,
      },
      {
        rel: 'manifest',
        href: '/manifest.json',
      },
      {
        rel: 'apple-touch-icon',
        href: '/logo192.png',
      },
    ],
  }),

  shellComponent: RootDocument,
})

// Simple error boundary to catch rendering errors
class ErrorBoundary extends React.Component<
  { children: ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div className="w-full max-w-md space-y-4 rounded-lg border border-border bg-card p-6 text-card-foreground shadow-lg">
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Something went wrong
              </h1>
              <p className="text-sm text-muted-foreground">
                The application encountered an unexpected error.
              </p>
            </div>
            <div className="rounded-md bg-destructive/10 p-3">
              <p className="text-sm font-mono text-destructive">
                {this.state.error.toString()}
              </p>
            </div>
            <button
              onClick={() => this.setState({ error: null })}
              className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
              Try again
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

function RootDocument({ children }: { children: React.ReactNode }) {
  // Always use 'en' and 'ltr' for SSR to prevent hydration mismatch
  // LanguageProvider will update these attributes after client mounts
  return (
    <html lang="en" dir="ltr">
      <head>
        <HeadContent />
      </head>
      <body>
        <QueryProvider>
          <LanguageProvider>
            <ThemeProvider>
              <AuthProvider>
                <ErrorBoundary>
                  <main className="min-h-screen">{children}</main>
                  <BottomNav />
                  <PWAUpdatePrompt />
                  <Toaster
                    position="top-center"
                    richColors
                    closeButton
                    duration={3000}
                  />
                </ErrorBoundary>
                <TanStackDevtools
                  config={{
                    position: 'bottom-right',
                  }}
                  plugins={[
                    {
                      name: 'Tanstack Router',
                      render: <TanStackRouterDevtoolsPanel />,
                    },
                    {
                      name: 'React Query',
                      render: <ReactQueryDevtools />,
                    },
                  ]}
                />
              </AuthProvider>
            </ThemeProvider>
          </LanguageProvider>
        </QueryProvider>
        <Scripts />
      </body>
    </html>
  )
}
